import { selectUser } from "../../../redux/user/userSlice";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../app/hooks";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { retrieveStudent, selectStudent, StudentInfo, studentSlice } from "../../../redux/student/studentSlice";
import PageAuthenticatedLoading from "../../../features/pages/PageAuthenticatedLoading";
import StudentPageTitle from "../basics/StudentPageTitle";
import StudentPageAvatar from "../basics/StudentPageAvatar";
import { ContextMenu, ContextMenuOptions } from "../../../features/base/ContextMenu";
import { imageInsSlice } from "../../../redux/image/imageInsSlice";
import FragmentCardError from "../../../features/base/FragmentCardError";
import StudentSchoolClassroomList from "./StudentSchoolClassroomList";
import { BreadcrumbInfo, TopBar } from "../../../features/top/TopBar";
import { retrieveSchool, selectSchool } from "../../../redux/school/schoolSlice";
import { retrieveClassroom, selectClassroom } from "../../../redux/classroom/classroomSlice";
import { isoToBrt, LoadState } from "../../../redux/common";
import MenuIcon from "@mui/icons-material/Menu";
import { anyPermission, checkIsAdmin, hasPermission, PermissionId } from "../../../app/permissions";
import { routeStudent, routeStudentEdit, routeStudentImage } from "../../../routes/student";
import { studentInsUpdSlice } from "../../../redux/student/studentInsUpdSlice";
import StudentUsersList from "./StudentUsersList";
import { ElementMissingContext } from "../../../features/base/ElementMissingContext";
import StudentImages from "./StudentImages";
import { AppBar, Box, Button, ButtonGroup, Grid, Paper, Stack, Toolbar, Tooltip, Typography } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { studentDelete } from "../../../redux/student/studentInsUpdSlice";
import { routeSchool } from "../../../routes/school";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { useEffect, useState } from "react";
import { routeClassroom } from "../../../routes/classroom";

// import HomeIcon from "@mui/icons-material/Home";
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import CollectionsIcon from "@mui/icons-material/Collections";
import EditIcon from "@mui/icons-material/Edit";
import SchoolIcon from '@mui/icons-material/School';
import ShieldIcon from '@mui/icons-material/Shield';

import PrizaGridBar from "../../../features/base/grid/PrizaGridBar";
import TooltipButton from "../../../components/TooltipButton";

export const VALID_TABS = [
    "main",
    "classroom",
    "permissions",
    "images"
];

export type StudentContentOptions = typeof VALID_TABS[number];

export function Student() {
    const user = useSelector(selectUser);
    const student = useSelector(selectStudent);
    const school = useSelector(selectSchool);
    const classroom = useSelector(selectClassroom);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Delete dialog state
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string|null>(null);

    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);
        setDeleteError(null);
    };

    const canDeleteImage = () => {
        if(hasPermission(user.priza.info, PermissionId.CLASSROOM_ADMIN, classroomId)) {
            return true;
        }
        if(hasPermission(user.priza.info, PermissionId.SCHOOL_ADMIN, schoolId)) {
            return true;
        }
        return false;
    };


    const handleDeleteConfirm = async () => {
        setDeleting(true);
        setDeleteError(null);
        try {
            if (!studentId || !schoolId) {
                setDeleteError("Dados insuficientes para remover o aluno.");
                setDeleting(false);
                return;
            }
            await dispatch(studentDelete({ studentId }));
            setOpenDeleteDialog(false);
            navigate(routeSchool(schoolId, "classroom"));
        } catch (err: any) {
            setDeleteError("Erro ao remover aluno. Tente novamente.");
            setDeleting(false);
        }
    };

    // Function to get initial tab from URL hash
    const getInitialTabFromHash = (): StudentContentOptions => {
        const hash = location.hash.replace('#', '');
        if (VALID_TABS.includes(hash as StudentContentOptions)) {
            return hash as StudentContentOptions;
        }
        return "main";
    };

    const [contentSelected, setContentSelected] = useState<StudentContentOptions>(getInitialTabFromHash());

    const { studentId, schoolId, classroomId } = useParams();

    useEffect(() => {
        if (student.selected && student.selected.id != studentId) {
            dispatch(studentSlice.actions.clean());
        }
        if (student.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveStudent({ studentId: studentId }));
        }
    }, [studentId, student, user]);

    // Listen for hash changes to update content selection
    useEffect(() => {
        const handleHashChange = () => {
            const newTab = getInitialTabFromHash();
            setContentSelected(newTab);
        };

        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);

        // Also update on location change (for initial load or navigation)
        const newTab = getInitialTabFromHash();
        if (newTab !== contentSelected) {
            setContentSelected(newTab);
        }

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [location.hash, contentSelected]);

    if (!studentId || !schoolId) {
        return <ElementMissingContext />;
    }
    
    const studentAdmin = anyPermission(user.priza.info, schoolId, [ PermissionId.SCHOOL_ADMIN, PermissionId.SCHOOL_TEACHER ])
        || anyPermission(user.priza.info, classroomId, [ PermissionId.CLASSROOM_ADMIN, PermissionId.CLASSROOM_TEACHER ])
        || anyPermission(user.priza.info, studentId, [ PermissionId.STUDENT_ADMIN ]);

    const name = student.selected?.nickname || student.selected?.name;

    // Function to handle tab change and update URL hash
    const handleTabChange = (tab: string) => {
        if (VALID_TABS.includes(tab)) {
            setContentSelected(tab);
            window.location.hash = tab;
        } else {
            setContentSelected("main");
            window.location.hash = "main";
        }
    };

    const options: ContextMenuOptions[] = [];
    options.push({
        label: "Atualizar", click: () => {
            dispatch(studentSlice.actions.clean());
            navigate(routeStudent(schoolId, studentId));
        }
    });
    if (studentAdmin) {
        options.push({
            label: "Editar", click: () => {
                if (student.selected) {
                    const newStudent = { ...student.selected };
                    dispatch(studentInsUpdSlice.actions.set(newStudent));
                    navigate(routeStudentEdit(schoolId, studentId));
                }
            }
        });
        options.push({
            label: "Alterar Imagem", click: () => {
                if (student.selected) {
                    dispatch(imageInsSlice.actions.clean());
                    navigate(routeStudentImage(schoolId, studentId));
                }
            }
        });
        options.push({
            label: "Remover", click: () => setOpenDeleteDialog(true)
        });
    }
    const topMenu = <ContextMenu label={<MenuIcon />} options={options} />;

    const breadcrumbs: BreadcrumbInfo[] = [{ label: name || "Aluno" }];

    if (schoolId) {
        if (school.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveSchool({ schoolId: schoolId }));
        }
        breadcrumbs.push({
            label: school.selected?.name || "Escola",
            href: routeStudent(schoolId, studentId)
        });
    }
    if (classroomId) {
        if (classroom.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveClassroom({ classroomId: classroomId }));
        }
        breadcrumbs.push({
            label: classroom.selected?.name || "Sala de aula",
            href: routeStudent(schoolId, studentId, classroomId)
        });
    }

    return <>
        <Dialog open={openDeleteDialog} onClose={handleDeleteCancel} aria-labelledby="delete-student-dialog-title">
            <DialogTitle id="delete-student-dialog-title" >Remover Aluno</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Esta ação é <b>irreversível</b> e todos os dados associados serão perdidos!
                </DialogContentText>
                <DialogContentText sx={{ color: 'error.main' }}>
                    Tem certeza que deseja <b>REMOVER</b> o aluno <b>{student.selected?.name}</b>?
                </DialogContentText>
                {deleteError && <DialogContentText color="error">{deleteError}</DialogContentText>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDeleteCancel} 
                    color="primary" variant="contained" disabled={deleting}>Cancelar</Button>
                <Button onClick={handleDeleteConfirm} 
                    color="error" variant="contained" disabled={deleting} autoFocus>
                    {deleting ? "Removendo..." : "Remover"}
                </Button>
            </DialogActions>
        </Dialog>
        <PageAuthenticatedLoading
        topbar={<TopBar topMenu={topMenu}
            title={<Typography variant="h6">Aluno</Typography>}
            breadcrumbs={breadcrumbs}
        />}
        loading={{
            states: [student.state],
            content: () => {
                if (studentId && schoolId) {
                    const body = () => {
                        switch (contentSelected) {
                            case "main": return <Stack direction="row" useFlexGap
                                sx={{ flexWrap: "wrap", mb: 10 }} >
                                <Box sx={{ minWidth: "500px", width: "100%", m: 1 }}>
                                    <Paper sx={{ width: "100%", minWidth: "300px" }} elevation={3}>
                                        <PrizaGridBar toolbar={{ label: "Ficha" }} />
                                        <Grid container>
                                            <Grid>
                                                <Box sx={{ p: 2 }}>
                                                    <Typography variant="h6" component="div">
                                                        Nome
                                                    </Typography>
                                                    <Typography variant="body1" component="div">
                                                        {student.selected?.name || "Sem nome"}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid>
                                                <Box sx={{ p: 2 }}>
                                                    <Typography variant="h6" component="div">
                                                        Matricula
                                                    </Typography>
                                                    <Typography variant="body1" component="div">
                                                        {student.selected?.schoolNumber || ""}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid>
                                                <Box sx={{ p: 2 }}>
                                                    <Typography variant="h6" component="div">
                                                        Email
                                                    </Typography>
                                                    <Typography variant="body1" component="div">
                                                        {student.selected?.email || "-"}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid>
                                                <Box sx={{ p: 2 }}>
                                                    <Typography variant="h6" component="div">
                                                        Nascimento
                                                    </Typography>
                                                    <Typography variant="body1" component="div">
                                                        {isoToBrt(student.selected?.birthDate) || ""}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={12}>
                                                <Box sx={{ p: 2 }}>
                                                    <Typography variant="h6" component="div">
                                                        Texto livre
                                                    </Typography>
                                                    <Paper sx={{ p: 2 }} elevation={10}>
                                                        <Typography variant="body1" component="div">
                                                            <span dangerouslySetInnerHTML={{ __html: (student.selected?.freeText || "Sem descrição").replace(/\n/g, "<br />") }} />
                                                        </Typography>
                                                    </Paper>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Box>
                            </Stack>;
                            case "classroom": return <StudentSchoolClassroomList studentId={studentId} schoolId={schoolId} />;
                            case "permissions": return <StudentUsersList studentId={studentId} schoolId={schoolId} classroomId={classroomId} />;
                            case "images": return <StudentImages studentId={studentId} schoolId={schoolId} classroomId={classroomId} canDeleteImage={canDeleteImage()} />;
                        }
                    };
                    return <>
                        <AppBar position="static" color="default">
                            <Toolbar>
                                <StudentPageAvatar />
                                <StudentPageTitle studentId={studentId} />
                                <Box sx={{ flexGrow: 1 }} />
                                <Tooltip title="Voltar para a página anterior">
                                    <Button onClick={() =>
                                        classroomId
                                            ? navigate(routeClassroom(schoolId, classroomId, "main"))
                                            : navigate(routeSchool(schoolId, "classroom"))
                                    }>Voltar</Button>
                                </Tooltip>
                            </Toolbar>
                            <AppBar position="static" color="default" elevation={2}>
                            <Toolbar>
                                <ButtonGroup variant="contained" color="primary">
                                    <TooltipButton
                                        option="main"
                                        optionSelected={contentSelected}
                                        onClick={(option) => handleTabChange(option)}
                                        tooltip="Informações do Aluno">
                                        <PermIdentityIcon />
                                    </TooltipButton>
                                    <TooltipButton
                                        option="classroom"
                                        optionSelected={contentSelected}
                                        onClick={(option) => handleTabChange(option)}
                                        tooltip="Salas de Aula">
                                        <SchoolIcon />
                                    </TooltipButton>
                                    <TooltipButton
                                        option="permissions"
                                        optionSelected={contentSelected}
                                        disabled={!studentAdmin}
                                        onClick={(option) => handleTabChange(option)}
                                        tooltip="Responsáveis">
                                        <ShieldIcon />
                                    </TooltipButton>
                                    <TooltipButton
                                        option="images"
                                        optionSelected={contentSelected}
                                        onClick={(option) => handleTabChange(option)}
                                        tooltip="Imagens">
                                        <CollectionsIcon />
                                    </TooltipButton>
                                    <TooltipButton
                                        option="edit"
                                        optionSelected={contentSelected}
                                        disabled={!studentAdmin}
                                        onClick={() => {
                                            const newStudent = { ...student.selected } as StudentInfo;
                                            dispatch(studentInsUpdSlice.actions.set(newStudent));
                                            navigate(routeStudentEdit(schoolId, studentId));
                                        }}
                                        tooltip="Editar Aluno">
                                        <EditIcon />
                                    </TooltipButton>
                                </ButtonGroup>
                            </Toolbar>
                            </AppBar>
                        </AppBar>
                        {body()}
                    </>;
                } else {
                    return <FragmentCardError error="Informação de estudande faltando" />;
                }
            },
            error: "Erro ao carregar dados do aluno",
            retry: () => {
                dispatch(studentSlice.actions.clean());
            }
        }} />;
    </>;
}
